import React from 'react';
import { EmailOAuthAccount } from '@dreamai/types';
import { ReactComponent as InboxIcon } from '../../../icons/inbox-01.svg';
import { ReactComponent as LinkIcon } from '../../../icons/link-04.svg';
import { ReactComponent as LinkBrokenIcon } from '../../../icons/link-broken-01.svg';
import { ReactComponent as AlertTriangleIcon } from '../../../icons/alert-triangle-outline.svg';
import { ReactComponent as PasscodeIcon } from '../../../icons/passcode.svg';
import FreeTextGuidanceBox from './FreeTextGuidanceBox';

export interface InboxAccount extends EmailOAuthAccount {
  /**
   * Connection status. Only populated once the backend exposes it on the
   * accounts payload; undefined is treated as connected.
   */
  isActive?: boolean;
}

interface InboxesTabProps {
  accounts: InboxAccount[];
  /** oauthId whose labelling toggle is mid-flight, or null. */
  togglingOauthId: number | null;
  onToggleLabelling: (account: InboxAccount) => void;
  onSaveArchivingExceptions: (
    account: InboxAccount,
    value: string | null,
  ) => void;
  onConnectInbox: () => void;
  onReconnectInbox: (account: InboxAccount) => void;
}

const ARCHIVING_EDIT_PLACEHOLDER =
  'e.g. Never archive anything from the board, even if it looks automated.';

const ARCHIVING_EXAMPLES = [
  'Never archive anything from the board',
  'Always archive courier delivery updates',
  "Don't archive anything I haven't replied to",
];

const InboxesTab: React.FC<InboxesTabProps> = ({
  accounts,
  togglingOauthId,
  onToggleLabelling,
  onSaveArchivingExceptions,
  onConnectInbox,
  onReconnectInbox,
}) => (
  <div className='inbox-tab inboxes-tab'>
    <div className='section-header'>
      <h2>
        <span className='label-wide'>Connected inboxes</span>
        <span className='label-narrow'>Inboxes</span>
      </h2>
      <span className='spacer' />
      <button
        type='button'
        className='btn btn-icon'
        onClick={onConnectInbox}
        data-testid='connect-inbox'
      >
        <InboxIcon />
        Connect inbox
      </button>
    </div>

    {accounts.length === 0 ? (
      <div className='app-empty-state' data-testid='inboxes-empty'>
        <InboxIcon className='app-empty-icon' />
        <p className='app-empty-title'>
          You don&apos;t have any connected inboxes
        </p>
        <p className='app-empty-blurb'>
          Take away your email headache. Choose &lsquo;Connect inbox&rsquo; to
          add an inbox
        </p>
      </div>
    ) : (
      accounts.map((account) => {
        const disconnected = account.isActive === false;
        return (
          <div
            key={account.oauthId}
            className={`inbox-card${disconnected ? ' is-disconnected' : ''}`}
            data-testid={`inbox-card-${account.oauthId}`}
          >
            <div className='inbox-card-head'>
              <span className='inbox-avatar'>
                {disconnected ? <LinkBrokenIcon /> : <LinkIcon />}
              </span>
              <div className='inbox-id'>
                <span className='inbox-email'>{account.email}</span>
                {disconnected && (
                  <span className='inbox-disconnected'>
                    <AlertTriangleIcon />
                    Account disconnected
                    <button
                      type='button'
                      className='btn-link'
                      onClick={() => onReconnectInbox(account)}
                      data-testid={`reconnect-${account.oauthId}`}
                    >
                      Reconnect
                    </button>
                  </span>
                )}
              </div>
              <div className='inbox-card-toggle'>
                <span className='toggle-label'>Auto-tag &amp; archive</span>
                <button
                  className={`toggle-switch ${account.emailLabelling && !disconnected ? 'on' : 'off'}`}
                  onClick={() => onToggleLabelling(account)}
                  disabled={togglingOauthId !== null || disconnected}
                  aria-label={`Toggle auto-tagging for ${account.email}`}
                >
                  <span className='toggle-thumb' />
                </button>
              </div>
            </div>
            <div className='inbox-card-body'>
              <FreeTextGuidanceBox
                value={account.archivingExceptions}
                onSave={(value) => onSaveArchivingExceptions(account, value)}
                label='Archiving exceptions'
                editPlaceholder={ARCHIVING_EDIT_PLACEHOLDER}
                exampleChips={ARCHIVING_EXAMPLES}
                saveLabel='Save exception'
                clearLabel='Clear exceptions'
                testId={`archiving-exceptions-${account.oauthId}`}
                renderEmpty={(startEditing) => (
                  <button
                    type='button'
                    className='inbox-card-action'
                    onClick={startEditing}
                    data-testid={`add-exception-${account.oauthId}`}
                  >
                    <PasscodeIcon />
                    Add exception rules
                  </button>
                )}
              />
            </div>
          </div>
        );
      })
    )}
  </div>
);

export default InboxesTab;
